import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { useIsMobile } from '../../hooks';

function Marquee({
  children,
  speed = 20,
  direction = 'left',
  fade = false,
  distance = '50%',
}) {
  const isMobile = useIsMobile('sm');
  const applyFade = fade && !isMobile;
  const fadeStyleToApply = {
    content: '""',
    position: 'absolute',
    top: 0,
    width: '10rem',
    height: '100%',
    zIndex: 2,
    pointerEvents: 'none',
  };
  const fadeStyle = applyFade ? {
    position: 'relative',
    '&::before': {
      left: 0,
      background: 'linear-gradient(to right, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))',
      ...fadeStyleToApply,
    },
    '&::after': {
      right: 0,
      background: 'linear-gradient(to left, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))',
      ...fadeStyleToApply,
    },
  } : {};
  const animationId = `marquee-${direction}-${speed}-${distance.replace(/%/g, 'pct')}`;
  const keyframes = useMemo(() => {
    if (direction === 'left') {
      return {
        [`@keyframes ${animationId}`]: {
          '0%': { transform: 'translateX(0%)' },
          '100%': { transform: `translateX(-${distance})` },
        },
      };
    }
    return {
      [`@keyframes ${animationId}`]: {
        '0%': { transform: `translateX(-${distance})` },
        '100%': { transform: 'translateX(0%)' },
      },
    };
  }, [animationId, direction, distance]);

  return (
    <Box
      sx={{
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        ...fadeStyle,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          whiteSpace: 'nowrap',
          alignItems: 'center',
          width: 'fit-content',
          animation: `${animationId} ${speed}s linear infinite`,
          ...keyframes,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {children}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

Marquee.propTypes = {
  children: PropTypes.node.isRequired,
  speed: PropTypes.number,
  direction: PropTypes.string,
  fade: PropTypes.bool,
  distance: PropTypes.string,
};

Marquee.defaultProps = {
  speed: 20,
  direction: 'right',
  fade: false,
  distance: '50%',
};

export default Marquee;
